import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import React from 'react'
import { GoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useNavigate } from 'react-router-dom-v5-compat'

import AccountForm from '@/app/module/auth/component/common/account'
import ProfileForm from '@/app/module/auth/component/common/profile'
import { AccountFormValues, ProfileFormValues, SignupValues } from '@/app/module/auth/component/common/specs'
import { useSignup } from '@/app/module/auth/hooks/use-signup'
import { captureError } from '@/app/service/util/error'

type Props = {
  email: string
  emailToken: string
}

const SignupForm: React.FC<Props> = ({ email, emailToken }) => {
  const navigate = useNavigate()
  const [signup, { isLoading }] = useSignup()
  const [step, setStep] = React.useState(0)
  const [captchaDisplayed, setCaptchaDisplayed] = React.useState(false)
  const [formData, setFormData] = React.useState<Partial<SignupValues>>({})

  const handleProfileSubmit = (data: ProfileFormValues) => {
    setFormData((s: Partial<SignupValues>) => ({ ...s, ...data }))
    setStep(1)
  }

  const handleAccountSubmit = (data: AccountFormValues) => {
    setFormData((s: Partial<SignupValues>) => ({ ...s, ...data }))
    setCaptchaDisplayed(true)
  }
  const handleAccountBack = (data: AccountFormValues) => {
    setFormData((s: Partial<SignupValues>) => ({ ...s, ...data }))
    setStep((s) => (s ? s - 1 : s))
  }

  const handleCaptchaVerify = async (verifyToken: string) => {
    setCaptchaDisplayed(false)

    const signupData: SignupValues = {
      ...(formData as ProfileFormValues & AccountFormValues),
      verifyToken,
      emailToken: emailToken || '',
    }

    const result = await signup(signupData)
    if (result && !(result instanceof Error)) {
      navigate('/')
    }
  }

  React.useEffect(() => {
    // Show reCAPTCHA badge when component mounts
    if ((window as any).grecaptcha) {
      try {
        const badge = document.querySelector('.grecaptcha-badge')
        if (badge && badge.parentNode && badge.parentNode instanceof HTMLElement) {
          badge.parentNode.style.display = 'block'
        }
      } catch (e) {
        captureError(e)
      }
    }

    // Hide reCAPTCHA badge when component unmounts
    return () => {
      try {
        const badge = document.querySelector('.grecaptcha-badge')
        if (badge && badge.parentNode && badge.parentNode instanceof HTMLElement) {
          badge.parentNode.style.display = 'none'
        }
      } catch (e) {
        captureError(e)
      }
    }
  }, [])

  return (
    <StyledCard>
      <IconContainer>
        <StyledIcon>person</StyledIcon>
      </IconContainer>

      <CardContent>
        {step === 0 && <ProfileForm data={formData} mode="signup" onSubmit={handleProfileSubmit} />}
        {step === 1 && (
          <AccountForm
            data={formData}
            email={email}
            isLoading={isLoading}
            onBack={handleAccountBack}
            onSubmit={handleAccountSubmit}
          />
        )}
      </CardContent>

      {captchaDisplayed && <GoogleReCaptcha onVerify={handleCaptchaVerify} />}
    </StyledCard>
  )
}

const StyledCard = styled(Card)({
  maxWidth: '400px',
  margin: 'auto',
  marginTop: 'calc(60px + 5%)',
  marginBottom: '3rem',
  overflow: 'visible',
})

const IconContainer = styled(CardContent)({
  textAlign: 'center',
})

const StyledIcon = styled(Icon)(({ theme }) => ({
  fontSize: '4rem !important',
  color: theme.palette.primary.main,
}))

export default SignupForm

export type { Props as SignupFormProps }
